import { call, delay, fork, put, select, take } from "redux-saga/effects";

import { RootState } from "store";

// Lib
import * as actionsCreatorThesaurus from "./reducer";
import { filterByKey, TBaData, TChildren } from "./reducer";
import { filteredDataPrices, optionsPrices } from "lib/helpers";
import { convertDataByKey, getClearedArrayDublicate, sortedArray } from "lib/thesaurus";

// Actions / Type
import { showMessage } from "reducers/messages";
import { getTimeSuccess } from "pages/Timer/reducer";
import * as actionsCreator from "pages/Thesaurus/reducer";
import { TypeTarget } from "pages/Thesaurus/type";

type TMap = {
  [index: string]: string | number | boolean
}
type TOptions = {
  label?: string;
  value?: string;
};


// Start Sagas
export function* thesaurusSagaStart(api: any, action: any) {
  yield fork(getThesaurusByIdWatcher, api, action);
  yield fork(getBaDataForTargetsWatcher, api, action);
  yield fork(getTargetDetailsByIdWatcher, api, action);
}

function* _select<T>(fn: (state: RootState) => T) {
  const res: T = yield select(fn);
  return res;
}

export const settings = (store: RootState) => store.settings;
export const thesaurus = (store: RootState) => store.thesaurus;

/**
 * Get thesaurus by id Watcher
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getThesaurusByIdWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      let newData: TChildren[] = [];
      const actions: any = yield take(
        actionsCreator.getThesaurusById.type
      );

      const { pos, resolve, familyId } = actions.payload;

      const res = yield call(
        api.request,
        "getThesaurusById",
        { id: familyId || 0 }
      );

      if (!res?.ok) {
        yield put(actionsCreator.getThesaurusByIdError());
      } else {
        if (!!familyId) {
          const state = yield* _select(thesaurus);
          const initState: TChildren[] = structuredClone(state.data);

          let currentElement: any = initState;
          pos.forEach((posIndex: string, i: number) => {
            if (i === 0) {
              return;
            }
            currentElement = Array.isArray(currentElement)
              ? currentElement[+posIndex]
              : currentElement?.children[+posIndex];
          });

          currentElement.children = res?.data?.children;

          newData = initState;
        } else {
          newData = res.data.children;
        }

        yield put(actionsCreator.getThesaurusByIdSuccess(newData));
        if (resolve) {
          resolve("");
        }
      }
    } catch (e) {
      console.log("CATCH ERROR getThesaurusByIdWatcher: ", e);
    }
  }
}

/**
 * Get Ba data for targets Watcher
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getBaDataForTargetsWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      yield take(
        actionsCreator.getBaDataForTargets.type
      );

      yield put(getTimeSuccess({ data: "start", key: "time" }));

      const state = yield* _select(thesaurus);
      const settingsState = yield* _select(settings);
      const px = state.px;
      const tags = state.tags;

      const targetIds = tags.map((item: TypeTarget) => item.targetId);

      const formData = {
        px: px ? +px : 0,
        targetIds: targetIds
      };

      const res = yield call(api.request, "getBaDataTargets", formData);

      yield put(delay(300));

      if (!res.ok) {
        yield put(actionsCreator.getBaDataForTargetsError());
        yield put(getTimeSuccess({ data: "stop", key: "time" }));
      } else {
        const data = res?.data;

        if (!data.length) {
          yield put(actionsCreator.handlerChangeNoData(true));
          const uniqId = crypto.randomUUID();
          yield put(showMessage({
            type: "info",
            uniqId: uniqId,
            text: "No data available"
          }));
        }

        const convertObject = (item: TMap) => {
          return {
            value: "poa",
            mg: item.mg || "",
            label: `POA/${item.mg}mg`
          };
        };

        const result = data.map((item: TBaData) => {
          let titlePOA = "";
          let countPrice = 0;

          const optionsPr = item?.prices?.length ? item?.prices[0].prices?.length
            ? filteredDataPrices(item?.prices[0].prices)?.map((item: TMap) =>
              optionsPrices(item)
            ) : [] : [];
          let updateOptions: TMap[] = optionsPr;

          updateOptions = optionsPr.map((item: TMap) => {
            if (item.value === undefined) {
              countPrice += 1;

              return {
                ...item,
                ...convertObject(item)
              };
            }
            return item;
          });

          if (updateOptions.length === countPrice) {
            updateOptions = updateOptions.map((item: TMap) => convertObject(item));
            titlePOA = "Price on asking";
          }

          return {
            ...item,
            quantity: 1,
            selected: false,
            titlePOA: titlePOA,
            currentTypePrice: "EBC",
            optionsPrices: updateOptions,
            px: item?.tarPx?.match(/\d+\.\d+/g),
            currentPrice: settingsState.currentPrice,
            isPOA: updateOptions.length === countPrice,
          };
        });

        const dataDrugs = filterByKey({ data: result, key: "drug" });
        const dataImpurity = filterByKey({ data: result, key: "impurity" });
        const dataChemicalProbe = filterByKey({ data: result, key: "chemicalProbe" });

        const getTarAction = (data: TBaData[]) => data?.length
          ? data.map(
            (item: TBaData) => item.tarAction
          ).filter((tar): tar is string => tar !== undefined)
          : [];

        const getCleanedArray = (data: string[]) => {
          return data?.length ? [...new Set(
            data
              .filter((item: string) => !item.includes("-"))
              .flatMap((item: string) => item.split(" | "))
          )] : [];
        };

        const convertOptions = (data: string[]) => {
          return data?.length ? data?.map((item: string) => ({
            label: item,
            value: item
          })) : [];
        };

        const filteredTarActionDrugs: string[] = getTarAction(dataDrugs || []);
        const filteredTarActionCompounds: string[] = getTarAction(result || []);
        const filteredTarActionImpurity: string[] = getTarAction(dataImpurity || []);
        const filteredTarActionChemicalProbe: string[] = getTarAction(dataChemicalProbe || []);

        const cleanedArrayDrugs: string[] = getCleanedArray(filteredTarActionDrugs);
        const cleanedArrayImpurity: string[] = getCleanedArray(filteredTarActionImpurity);
        const cleanedArrayCompounds: string[] = getCleanedArray(filteredTarActionCompounds);
        const cleanedArrayChemicalProbe: string[] = getCleanedArray(filteredTarActionChemicalProbe);

        const optionsTarPxDrugs: TOptions[] = convertDataByKey(dataDrugs, "tarPx");
        const optionsTarPxCompounds: TOptions[] = convertDataByKey(result, "tarPx");
        const optionsTarPxImpurity: TOptions[] = convertDataByKey(dataImpurity, "tarPx");
        const optionsTarPxChemicalProbe: TOptions[] = convertDataByKey(dataChemicalProbe, "tarPx");

        const optionsTarActionDrugs: TOptions[] = convertOptions(cleanedArrayDrugs);
        const optionsTarActionImpurity: TOptions[] = convertOptions(cleanedArrayImpurity);
        const optionsTarActionCompounds: TOptions[] = convertOptions(cleanedArrayCompounds);
        const optionsTarActionChemicalProbe: TOptions[] = convertOptions(cleanedArrayChemicalProbe);

        const filteredOptionsDublicateTarPxDrugs = sortedArray({
          data: getClearedArrayDublicate(optionsTarPxDrugs, "value")
        });
        const filteredOptionsDublicateTarPxImpurity = sortedArray({
          data: getClearedArrayDublicate(optionsTarPxImpurity, "value")
        });
        const filteredOptionsDublicateTarPxCompounds = sortedArray({
          data: getClearedArrayDublicate(optionsTarPxCompounds, "value")
        });
        const filteredOptionsDublicateTarPxChemicalProbe = sortedArray({
          data: getClearedArrayDublicate(optionsTarPxChemicalProbe, "value")
        });

        const filteredOptionsDublicateTarActionDrugs = sortedArray({
          isAlphabet: true,
          data: optionsTarActionDrugs
        });
        const filteredOptionsDublicateTarActionImpurity = sortedArray({
          isAlphabet: true,
          data: optionsTarActionImpurity
        });
        const filteredOptionsDublicateTarActionCompounds = sortedArray({
          isAlphabet: true,
          data: optionsTarActionCompounds
        });
        const filteredOptionsDublicateTarActionChemicalProbe = sortedArray({
          isAlphabet: true,
          data: optionsTarActionChemicalProbe
        });

        const optionsAvailability: TOptions[] = [
          actionsCreatorThesaurus.initialStateSelectors,
          { label: "In Stock/POA", value: "ON_STOCK_POA" },
          { label: "In Stock", value: "ON_STOCK" },
          { label: "Out of stock", value: "OUT_OF_STOCK" }
        ];

        filteredOptionsDublicateTarPxDrugs.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarPxImpurity.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarPxCompounds.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarPxChemicalProbe.unshift(actionsCreatorThesaurus.initialStateSelectors);

        filteredOptionsDublicateTarActionDrugs.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarActionImpurity.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarActionCompounds.unshift(actionsCreatorThesaurus.initialStateSelectors);
        filteredOptionsDublicateTarActionChemicalProbe.unshift(actionsCreatorThesaurus.initialStateSelectors);

        yield put(actionsCreator.getBaDataForTargetsSuccess({
          drugs: dataDrugs,
          compounds: result,
          impurities: dataImpurity,
          chemicalProbes: dataChemicalProbe,

          optionsAvailabilityCompounds: optionsAvailability,
          optionsTarPxCompounds: filteredOptionsDublicateTarPxCompounds,
          optionsTarActionCompounds: filteredOptionsDublicateTarActionCompounds,

          optionsAvailabilityDrugs: optionsAvailability,
          optionsTarPxDrugs: filteredOptionsDublicateTarPxDrugs,
          optionsTarActionDrugs: filteredOptionsDublicateTarActionDrugs,

          optionsAvailabilityImpurities: optionsAvailability,
          optionsTarPxImpurities: filteredOptionsDublicateTarPxImpurity,
          optionsTarActionImpurities: filteredOptionsDublicateTarActionImpurity,

          optionsAvailabilityChemicalProbes: optionsAvailability,
          optionsTarPxChemicalProbes: filteredOptionsDublicateTarPxChemicalProbe,
          optionsTarActionChemicalProbes: filteredOptionsDublicateTarActionChemicalProbe,
        }));
        yield put(getTimeSuccess({ data: "stop", key: "time" }));
        yield put(actionsCreator.handlerChangeAllPrices(settingsState.currentPrice));
      }
    } catch (e) {
      console.log("CATCH ERROR getBaDataForTargetsWatcher: ", e);
    }
  }
}

/**
 * Get target details by id Watcher
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getTargetDetailsByIdWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions: any = yield take(
        actionsCreator.getTargetDetailsById.type
      );

      const res: any = yield call(api.request, "getTargetDetailsById", actions.payload);

      yield put(delay(400));

      if (!res.ok) {
        yield put(actionsCreator.getTargetDetailsByIdError());
      } else {
        yield put(actionsCreator.getTargetDetailsByIdSuccess(res.data));
      }
    } catch (e) {
      console.log("CATCH ERROR getTargetDetailsByIdWatcher: ", e);
    }
  }
}
